import { useGet, usePost, usePut } from "@/apiHooks/HttpFunctions";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { Col, Row } from "react-bootstrap";

import { productClassesMasterDataApi } from "@/apis/apis";
import BasicInput from "@/components/BasicInput";
import RenderToast from "@/components/RenderToast";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import useLocaleCodes from "@/CustomHooks/useLocaleCodes";
import { getProductClassInitialValues } from "@/data/WebDataManagement/MasterData/ProductClasses/initial-values";
import BasicModal from "@/Modals/BasicModal";
import getProductClassSchema from "@/schema/WebDataManagement/MasterData/ProductClasses/schema";
import { useFormik } from "formik";
import LocalizedInput from "@/components/LocalizedInput";
import StatusInput from "@/components/StatusInput";

const ProductClassModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });
  const isEdit = !!modalState.id;
  const localeCodes = useLocaleCodes();

  const showApi = isEdit
    ? `${productClassesMasterDataApi}/${modalState.id}`
    : null;
  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: isEdit,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Product Class ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePut({
    apiURL: `${productClassesMasterDataApi}/${modalState.id}`,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: productClassesMasterDataApi,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getProductClassInitialValues(
      localeCodes,
      isEdit ? showData : null
    ),
    validationSchema: getProductClassSchema(localeCodes),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    isEdit ? saveEdit(values) : saveCreate(values);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={isEdit ? "Edit Product Class" : "Add Product Class"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"code"}
            saveError={saveError}
            label="ID"
          />
        </Col>
      </Row>

      <LocalizedInput
        formik={formik}
        error={saveError}
        isEdit={!!modalState.id}
      />

      <Row>
        <Col md={12}>
          <StatusInput formik={formik} saveError={saveError} />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default ProductClassModal;
